import { keyEnter, pause, toggleSetValue } from "@src/Utils";
import { Button, shake } from "@src/components/Button";
import Modal, { swapContent } from "@src/components/Modal";
import { ContinuousProgressBar } from "@src/components/ProgressBar";
import {
  allDataPreset,
  quickSelectOptions,
  selectActions,
  userTransform,
} from "@src/constants/userData";
import { DisplayInfo, ModalContext } from "@src/contexts/Contexts";
import {
  IconCheck,
  IconDownload,
  IconExclamationCircle,
  IconPencil,
  IconPlus,
  IconX,
} from "@tabler/icons-react";
import clsx from "clsx";
import { useContext, useEffect, useState } from "react";
import Card, { ExpandableCard } from "./Card";
import CheckBox, { MasterCheckBox } from "./CheckBox";
import Tooltip from "./Tooltip";

const initialState = {
  downloaded: 0,
  totalUsers: 0,
  ready: false,
  saved: false,
  link: "",
  fileName: "",
  dataSize: null,
  willCancel: 0,
};

/**
 * @param {{
 *   type: string;
 *   openState: UseState<boolean>;
 *   onRetransform: function;
 *   onPause: function;
 *   onResume: function;
 *   onCancel: function;
 *   onOpen: function;
 *   dataStatus: any;
 * }} props
 */
const DownloadUsersModal = (props) => {
  const {
    type,
    openState: [isOpen, setIsOpen],
    onRetransform,
    onPause,
    onResume,
    onCancel,
    onOpen,
    dataStatus,
  } = props;

  const [downloadState, setDownloadState] = useState({ ...initialState });

  useEffect(() => {
    if (!dataStatus) return;

    const { type, fileName, link, totalUsers, downloaded } = dataStatus;

    switch (type) {
      case selectActions.JSON_READY:
        if (downloadState.link) URL.revokeObjectURL(downloadState.link);
        if (link === downloadState.link) return;

        setDownloadState({
          ...downloadState,
          downloaded,
          totalUsers,
          ready: true,
        });

        if (isOpen) {
          enableDownloadLink(link, fileName);
        } else if (link) {
          URL.revokeObjectURL(link);
        }
        return;

      case selectActions.JSON_ALL_STARTED:
        if (isOpen) {
          setDownloadState({ ...downloadState, totalUsers });
        }
        return;

      case selectActions.JSON_ALL_PROGRESS:
        if (isOpen && downloadState.downloaded !== downloaded) {
          setDownloadState({ ...downloadState, downloaded });
        }
        return;
    }
  }, [dataStatus]);

  function enableDownloadLink(link, fileName) {
    const units = ["B", "KB", "MB", "GB"];
    let unitIdx = 0;

    fetch(link)
      .then((res) => res.blob())
      .then((blob) => {
        let size = blob.size;

        while (size > 999) {
          size /= 1024;
          unitIdx += 1;
        }
        unitIdx = Math.min(unitIdx, 3);

        const dataSize = `${Math.round(100 * size) / 100} ${units[unitIdx]}`;

        setIsOpen((isOpen) => {
          if (!isOpen) return false;

          setDownloadState((prev) => ({
            ...prev,
            link,
            fileName,
            dataSize,
          }));

          return true;
        });
      });
  }

  function downloadJSON(link, fileName) {
    const linkBtn = document.createElement("a");
    linkBtn.href = link;
    linkBtn.download = `${fileName}.json`;
    document.body.appendChild(linkBtn);
    linkBtn.click();
    setDownloadState((prev) => ({ ...prev, saved: true }));

    document.body.removeChild(linkBtn);
    URL.revokeObjectURL(link);
  }

  async function onDownloadSave() {
    const { link, fileName } = downloadState;

    await pause(100);
    downloadJSON?.(link, fileName);
    await pause(1000);

    setIsOpen(false);
  }

  function onDownloadCancel(confirmed = type === selectActions.JSON_SELECTION) {
    setDownloadState((state) => {
      if (confirmed || !state.downloaded) {
        if (state.link) URL.revokeObjectURL(state.link);
        onCancel?.();
        setIsOpen(false);
      } else {
        state.willCancel += 1;
        onPause?.();
      }

      return { ...state };
    });
  }

  function onUndoDownloadCancel() {
    setDownloadState((state) => ({ ...state, willCancel: 0 }));
    onResume?.();
  }

  return (
    <Modal
      id="download-modal"
      className="download-modal"
      title={
        type === selectActions.JSON_SELECTION
          ? "Download Selection"
          : type === selectActions.JSON_ALL
            ? "Download All Users"
            : ""
      }
      contentLabel="Login Modal"
      isOpen={isOpen}
      close={() => onDownloadCancel()}
      afterClose={() => setDownloadState({ ...initialState })}
      focusOnOpen
    >
      <Download
        state={downloadState}
        onOpen={onOpen}
        onRetransform={onRetransform}
        onSave={onDownloadSave}
        onRequestCancel={() => onDownloadCancel()}
        onConfirmCancel={() => onDownloadCancel(true)}
        onUndoCancel={onUndoDownloadCancel}
      />
    </Modal>
  );
};

/**
 * @param {{
 *   state: typeof initialState;
 *   onOpen: function;
 *   onRetransform: function;
 *   onSave: function;
 *   onRequestCancel: function;
 *   onConfirmCancel: function;
 * }} props
 */
const Download = (props) => {
  const {
    state,
    onOpen,
    onRetransform,
    onSave,
    onRequestCancel,
    onConfirmCancel,
    onUndoCancel,
  } = props;

  const { isMobile, isTablet, isPortrait } = useContext(DisplayInfo);
  const { overlayElement, contentElement } = useContext(ModalContext);

  /** @type {{ [k in keyof userTransform]: Set<string> }} */
  const emptyTransform = Object.fromEntries(
    Object.keys(userTransform).map((key) => [key, new Set()]),
  );

  const [dataReady, setDataReady] = useState(state.ready);
  const [transformOpts, setTransformOpts] = useState(null);
  const [showFullTransform, setShowFullTransform] = useState(false);
  const [enabledQuickSelects, setEnabledQuickSelects] = useState(new Set());
  const [loadingRemap, setLoadingRemap] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const usersTxt = `${state.downloaded === 1 ? "User" : "Users"}`;
  const loadingDownload = !state.link || !dataReady || loadingRemap;

  useEffect(() => {
    /** @type {(keyof quickSelectOptions)[]} */
    const defaultPresets = ["userSummary"];
    const defaultTransform = presetUnion(defaultPresets);

    setTransformOpts({ ...defaultTransform });
    setEnabledQuickSelects(new Set(defaultPresets));
    onOpen?.(defaultTransform);
  }, []);

  useEffect(() => {
    if (loadingRemap) setLoadingRemap(false);
  }, [state.link]);

  useEffect(() => {
    if (state.ready) {
      if (showConfirm) return setDataReady(true);

      swapContent(contentElement, async () => {
        await pause(150);
        setDataReady(true);
        await pause(100);
      });
    }
  }, [state.ready]);

  useEffect(() => {
    if (showConfirm && state.willCancel) {
      shake(overlayElement?.firstChild, 500);
      return;
    }

    swapContent(contentElement, async () => {
      await pause(150);
      setShowConfirm(!!state.willCancel);
      setShowFullTransform(false);
      await pause(100);
    });
  }, [state.willCancel]);

  useEffect(() => {
    if (!state.link || !transformOpts) return;
    setLoadingRemap(true);
    onRetransform?.(transformOpts);

    checkSelectedPresets(transformOpts);
  }, [transformOpts]);

  function toggleIncludeField(category, fieldValue) {
    setTransformOpts((opts) => ({
      ...opts,
      [category]: toggleSetValue(opts[category], fieldValue),
    }));
  }

  function includeCategory(category) {
    setTransformOpts((opts) => ({
      ...opts,
      [category]: new Set(Object.keys(userTransform[category]?.fields)),
    }));
  }

  function excludeCategory(category) {
    setTransformOpts((opts) => ({
      ...opts,
      [category]: new Set(),
    }));
  }

  function includeAllData() {
    setEnabledQuickSelects(new Set([allDataPreset]));

    setTransformOpts((opts) => {
      Object.entries(userTransform).forEach(([category, set]) => {
        opts[category] = new Set(Object.keys(set?.fields));
      });

      return { ...opts };
    });
  }

  function addQuickSelect(preset) {
    if (preset === allDataPreset) return includeAllData();

    let allDataIncluded = false;
    setEnabledQuickSelects((presets) => {
      if (presets.has(allDataPreset)) {
        allDataIncluded = true;
        return new Set([preset]);
      }

      return toggleSetValue(presets, preset);
    });

    setTransformOpts((opts) => {
      const { fields } = quickSelectOptions[preset];

      Object.keys(userTransform).forEach((category) => {
        if (allDataIncluded) opts[category]?.clear();

        const oldFields = opts[category]?.values() || [];
        const newFields = fields[category] || [];
        opts[category] = new Set([...oldFields, ...newFields]);
      });

      return { ...opts };
    });
  }

  function removeQuickSelect(preset) {
    setEnabledQuickSelects((presets) => toggleSetValue(presets, preset));

    setTransformOpts((curOptions) => {
      const { fields } = quickSelectOptions[preset];

      const activePresets = presetUnion([...enabledQuickSelects.values()]);

      Object.keys(fields).forEach(
        /** @param {keyof fields} category */
        (category) => {
          fields[category].forEach((field) => {
            if (activePresets[category].has(field)) return;

            curOptions[category].delete(field);
          });
        },
      );

      return { ...curOptions };
    });
  }

  function checkSelectedPresets(opts) {
    setEnabledQuickSelects((presets) => {
      presets.forEach((preset) => {
        const presetOpts = quickSelectOptions[preset];
        if (!presetOpts) return;

        const { fields } = presetOpts;
        for (let category in fields) {
          const subPreset = fields[category];
          const subOpts = opts[category];

          if (subPreset.some((field) => !subOpts?.has(field))) {
            presets.delete(preset);
            break;
          }
        }
      });

      const setMap = Object.entries(quickSelectOptions.allData.fields);
      const allDataIncluded = setMap.every(([category, set]) =>
        set.every((field) => opts[category]?.has(field)),
      );
      if (allDataIncluded) return new Set([allDataPreset]);

      return presets;
    });
  }

  /** @param {(keyof quickSelectOptions)[]} presets */
  function presetUnion(presets = []) {
    const presetFields = presets
      .filter((preset) => preset !== allDataPreset)
      .map((preset) => quickSelectOptions[preset]?.fields);

    /** @type {typeof emptyTransform} */
    const union = {};

    Object.keys(emptyTransform).forEach((category) => {
      const aggregated = []
        .concat(...presetFields.map((preset) => preset[category]))
        .filter((v) => !!v);
      union[category] = new Set(aggregated);
    });

    return union;
  }

  const downloadLabel = (
    <>
      {isMobile && isPortrait ? "" : isTablet ? "JSON" : "Download JSON"}
      <span className="file-size">{state.dataSize}</span>
    </>
  );

  return (
    <>
      <p className="download-progress">
        {dataReady ? (
          <span>
            <b className="total">{state.downloaded}</b> {usersTxt} Total
          </span>
        ) : (
          <>
            <span>
              {showConfirm
                ? "Fetching Paused"
                : state.downloaded / state.totalUsers === 1
                  ? "Aggregating..."
                  : "Fetching Users..."}
            </span>

            {state.totalUsers > 0 && (
              <span>
                {state.downloaded} / {state.totalUsers} Users
              </span>
            )}
          </>
        )}
      </p>

      <ContinuousProgressBar
        className="download-progress-bar"
        initAnimate
        percentage={(100 * state.downloaded) / state.totalUsers}
      />

      {showConfirm && (
        <div className="cancel-warn">
          <IconExclamationCircle className="warn-icon" stroke={1.5} />

          <span className="warn-msg">
            <b className="warn-header">All fetched data will be discarded.</b>
            <p>
              You will need to start over if you want to download again. Are you
              sure you want to cancel?
            </p>
          </span>
        </div>
      )}

      {!showConfirm && dataReady && (
        <>
          <p className="download-ready">
            <span>Select Data to Include</span>

            <span className="info">
              The data in the JSON download can be customized below to include
              select info from the users and their tasks, modules, scenarios,
              and activities.
            </span>
          </p>

          <div className="quick-select">
            <span className="header">Quick Select Presets</span>

            {Object.entries(quickSelectOptions).map(([optKey, opt]) => (
              <Chip
                key={optKey}
                label={opt.label}
                active={enabledQuickSelects.has(optKey)}
                onClick={() =>
                  enabledQuickSelects.has(optKey)
                    ? removeQuickSelect(optKey)
                    : addQuickSelect(optKey)
                }
              />
            ))}
          </div>

          {Object.entries(userTransform).map(([category, set]) => (
            <MultiSelectCard
              key={category}
              set={transformOpts?.[category]}
              setName={category}
              className={!showFullTransform && "hidden"}
              header={set.label}
              options={set.fields}
              selectedText="Fields"
              onToggleOption={toggleIncludeField}
              onSelectAll={() => includeCategory(category)}
              onDeselectAll={() => excludeCategory(category)}
              inert={!showFullTransform}
            />
          ))}

          <TransformPreview
            transformOpts={transformOpts}
            fullTransformState={[showFullTransform, setShowFullTransform]}
          />
        </>
      )}

      <span className="btn-row">
        <Button
          className="cancel-download-file"
          variant="secondary"
          label={showConfirm ? "No, Continue" : "Cancel"}
          onClick={showConfirm ? onUndoCancel : onRequestCancel}
          styles={{ flex: showConfirm && 2 }}
        />
        <Button
          className={showConfirm ? "confirm-cancel" : "download-file"}
          variant={dataReady || showConfirm ? "light" : "secondary"}
          label={
            showConfirm
              ? "Yes, Stop Download"
              : state.saved
                ? "Saved"
                : downloadLabel
          }
          icon={
            showConfirm ? null : state.saved ? (
              <IconCheck stroke={2.8} scale={1.1} />
            ) : (
              <IconDownload stroke={2.6} />
            )
          }
          gap="0.6rem"
          loading={!showConfirm && loadingDownload}
          onClick={showConfirm ? onConfirmCancel : onSave}
          styles={{ flex: 3 }}
        />
      </span>
    </>
  );
};

/**
 * @param {{
 *   transformOpts: any;
 *   fullTransformState: UseState<boolean>;
 * }} props
 */
const TransformPreview = (props) => {
  const { transformOpts, fullTransformState } = props;

  const { contentElement } = useContext(ModalContext);

  const [showFullTransform, setShowFullTransform] = fullTransformState;
  const [transformPreview, setTransformPreview] = useState(null);
  const [previewCard, setPreviewCard] = useState(null);

  useEffect(() => {
    renderPreview(transformOpts);
    if (!showFullTransform) resizePreview(previewCard);
  }, [transformOpts]);

  useEffect(() => {
    resizePreview(previewCard);
  }, [previewCard]);

  function renderPreview(opts) {
    const preview = Object.keys(userTransform)
      .filter((category) => opts?.[category].size > 0)
      .map((category) => (
        <li key={category}>
          {userTransform[category].label}
          <b>({opts?.[category].size} Fields)</b>
        </li>
      ));

    const maxRows = 2;
    if (preview.length > maxRows) {
      const numExtra = preview
        .splice(maxRows)
        .map((item) => opts?.[item.key]?.size)
        .filter((n) => !isNaN(n))
        .reduce((a, b) => a + b);

      preview.push(
        <li key="more" className="no-bullet">
          + {numExtra} More
        </li>,
      );
    }

    if (!preview.length) {
      preview.push(
        <li key="none-included" className="no-bullet">
          No Data Included
        </li>,
      );
    }

    setTransformPreview(preview);
  }

  async function resizePreview(card) {
    const list = card?.querySelector("ul");

    if (!list) return;
    await pause(10);
    let newHeight = list.scrollHeight;

    const { paddingTop, paddingBottom } = window.getComputedStyle(card);
    newHeight += parseFloat(paddingTop) + parseFloat(paddingBottom);

    newHeight = Math.round(newHeight);
    card.style.minHeight = `${newHeight}px`;
    card.style.maxHeight = `${newHeight}px`;
  }

  function editTransformOpts(event) {
    /** @type {HTMLElement} */
    const card = event.currentTarget?.parentElement;

    if (!card || !card.classList.contains("transform-preview")) return;
    const height = card.clientHeight;

    setShowFullTransform(async (showTransform) => {
      if (!showTransform) {
        card.style.marginTop = `calc(-${height}px - 0.5rem)`;
        card.style.minHeight = `${height}px`;
        card.style.maxHeight = `${height}px`;

        if (contentElement) {
          contentElement.classList.add("expand-transform");
          await pause(1000);
          contentElement.classList.remove("expand-transform");
        }
      }

      return !showTransform;
    });
  }

  return (
    <Card
      className={clsx("transform-preview", showFullTransform && "hidden")}
      inert={showFullTransform}
      ref={(card) => {
        if (!transformOpts) return;
        if (!card || card.classList.contains("initialized")) return;

        card.classList.add("initialized");
        setPreviewCard(card);
      }}
    >
      {!showFullTransform && <ul>{transformPreview}</ul>}

      {contentElement && (
        <Tooltip
          className="edit-tooltip"
          content="Edit Fields"
          placement="left"
          appendTo={contentElement}
          delay={[500, 0]}
        >
          <IconPencil
            className="edit-transform"
            stroke={2.5}
            onClick={editTransformOpts}
            onKeyDown={(e) => keyEnter(e, editTransformOpts)}
          />
        </Tooltip>
      )}
    </Card>
  );
};

/**
 * @param {{
 *   set: Set;
 *   setName: string;
 *   header: string;
 *   className: CardProps["className"];
 *   options: object;
 *   selectedText: string;
 *   onToggleOption: function;
 *   onSelectAll: function;
 *   onDeselectAll: function;
 * }} props
 */
const MultiSelectCard = (props) => {
  const {
    set,
    setName,
    header,
    className,
    options,
    selectedText = "Selected",
    onToggleOption,
    onSelectAll,
    onDeselectAll,
    ...otherProps
  } = props;

  const [allSelected, setAllSelected] = useState(false);

  useEffect(() => {
    setAllSelected(Object.keys(options).every((opt) => set?.has(opt)));
  }, [set?.size]);

  return (
    <ExpandableCard
      className={clsx("data-multiselect", className)}
      header={
        <>
          <MasterCheckBox
            variant="dark"
            someSelected={set?.size > 0}
            allSelected={allSelected}
            onSelectAll={onSelectAll}
            onDeselectAll={onDeselectAll}
          />
          <span className={clsx("set-label", !set?.size && "faded")}>
            {header}
          </span>

          {set?.size > 0 && (
            <span className="num-selected">
              {set.size} {selectedText}
            </span>
          )}
        </>
      }
      numRows={Math.min(5, Object.keys(options).length)}
      {...otherProps}
    >
      {Object.entries(options).map(([option, optionLabel]) => (
        <CheckBox
          key={option}
          variant="dark"
          state={[set?.has(option), () => onToggleOption?.(setName, option)]}
          label={optionLabel}
        />
      ))}
    </ExpandableCard>
  );
};

/**
 * @param {{
 *   label: string;
 *   className: ButtonProps["className"];
 *   onClick: ButtonProps["onClick"];
 *   active: boolean;
 * }} props
 * @returns
 */
const Chip = (props) => {
  const { className, label, onClick, active } = props;

  return (
    <div
      className={clsx("chip", active && "active", className)}
      onClick={onClick}
      onKeyDown={(e) => keyEnter(e, onClick)}
      tabIndex={0}
    >
      <IconPlus className="add" />
      <span className="label">{label}</span>
      <IconX className="remove" />
    </div>
  );
};

export default DownloadUsersModal;
